import React, { useState } from 'react';
import {
    User, Building2, MapPin, UserCheck, Camera, Trash2, X, FileText, Save, Loader2
} from 'lucide-react';
import { ClientService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';
import { useToast } from './ToastContext';
import { ColoreCalendarioField } from './clienti/ColoreCalendarioField';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (client: any) => void;
    // Termine ricerca corrente: pre-popola il nome (privato) o la ragione sociale (azienda).
    initialSearchTerm?: string;
    // Sede da assegnare al cliente; se omesso si usa quella corrente / del responsabile.
    defaultSedeId?: number;
}

// Stesso form di NuovoClienteView, in modale, per creare un cliente senza
// lasciare la pagina corrente. Regole:
//  - Privato → indirizzo/CAP/città/prov sui campi nativi (residenza).
//  - Azienda → ragione sociale + sede legale sui campi nativi; persona fisica
//    (nome/cognome + residenza) sui campi *Referente; perContoDiTerzi forzato true.
//  - Documento d'identità obbligatorio salvo la spunta "Prosegui senza".
const FilePreviewItem = ({ file, onRemove, onPreview }: { file: File, onRemove: () => void, onPreview: (f: File) => void }) => {
    const isImage = file.type.startsWith('image/');
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (isImage) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [file, isImage]);

    return (
        <div className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-200 shadow-sm text-sm group">
            <div className="flex items-center gap-3 overflow-hidden flex-1 cursor-pointer" onClick={() => onPreview(file)}>
                {isImage && previewUrl ? (
                    <img src={previewUrl} alt={file.name} className="w-10 h-10 object-cover rounded border border-gray-200" />
                ) : (
                    <div className="w-10 h-10 bg-gray-50 flex items-center justify-center rounded border border-gray-200">
                        <FileText size={20} className="text-gray-400" />
                    </div>
                )}
                <span className="truncate font-medium text-gray-700 group-hover:text-blue-600 transition-colors">{file.name}</span>
            </div>
            <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-red-500 p-2 hover:bg-red-50 rounded-lg ml-2 transition-colors">
                <Trash2 size={16} />
            </button>
        </div>
    );
};

const buildEmptyForm = () => ({
    tipo: 'Privato' as 'Privato' | 'Azienda',
    coloreCalendario: '', // '' = automatico
    nome: '',
    surname: '',
    ragioneSociale: '',
    pec: '',
    email: '',
    telefono: '',
    codiceFiscale: '',
    partitaIva: '',
    codiceSdi: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    luogoNascita: '',
    dataNascita: '',
    docTipo: 'Carta Identità',
    docNumero: '',
    docRilascioDa: '',
    docDataRilascio: '',
    docDataScadenza: '',
    indirizzoReferente: '',
    cittaReferente: '',
    capReferente: '',
    provinciaReferente: '',
    emailReferente: '',
    telefonoReferente: '',
    perContoDiTerzi: false,
    denominazioneTerzi: '',
    sedeTerzi: '',
    capTerzi: '',
    comuneTerzi: '',
    provinciaTerzi: '',
    pivaOrCfTerzi: '',
    codiceSdiTerzi: '',
    emailTerzi: '',
});

export const ClienteCreateModal: React.FC<Props> = ({ isOpen, onClose, onCreated, initialSearchTerm, defaultSedeId }) => {
    const { user, currentSedeId } = useAuth();
    const { addToast } = useToast();

    const [formData, setFormData] = useState<ReturnType<typeof buildEmptyForm>>(buildEmptyForm());
    const [clientFiles, setClientFiles] = useState<File[]>([]);
    const [bypassDoc, setBypassDoc] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [previewFile, setPreviewFile] = useState<File | null>(null);

    React.useEffect(() => {
        if (!isOpen) return;
        const base = buildEmptyForm();
        const trimmed = (initialSearchTerm || '').trim();
        if (trimmed) {
            // Si assume Privato: il testo va nel nome.
            base.nome = trimmed;
        }
        setFormData(base);
        setClientFiles([]);
        setBypassDoc(false);
    }, [isOpen, initialSearchTerm]);

    const set = (field: keyof ReturnType<typeof buildEmptyForm>, value: any) =>
        setFormData(prev => ({ ...prev, [field]: value }));

    const handleTipoChange = (nuovoTipo: 'Privato' | 'Azienda') => {
        if (nuovoTipo === formData.tipo) return;
        if (nuovoTipo === 'Privato') {
            setFormData(prev => ({
                ...prev,
                tipo: 'Privato',
                ragioneSociale: '', partitaIva: '', codiceSdi: '', pec: '',
                perContoDiTerzi: false,
                denominazioneTerzi: '', sedeTerzi: '', capTerzi: '', comuneTerzi: '',
                provinciaTerzi: '', pivaOrCfTerzi: '', codiceSdiTerzi: '', emailTerzi: '',
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                tipo: 'Azienda',
                // L'azienda agisce sempre per conto di se stessa.
                perContoDiTerzi: true,
                denominazioneTerzi: '', sedeTerzi: '', capTerzi: '', comuneTerzi: '',
                provinciaTerzi: '', pivaOrCfTerzi: '', codiceSdiTerzi: '', emailTerzi: '',
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!bypassDoc && clientFiles.length === 0) {
            addToast("È obbligatorio acquisire il documento d'identità, oppure spunta la casella per proseguire senza.", "error");
            return;
        }
        // Sede effettiva
        let sedeId: number | undefined = defaultSedeId;
        if (!sedeId && typeof currentSedeId === 'number') sedeId = currentSedeId;
        if (!sedeId && user?.role !== UserRole.ADMIN && user?.sedeId) sedeId = user.sedeId;
        if (user?.role === UserRole.ADMIN && !sedeId) {
            addToast("Devi selezionare una sede per il cliente", "error");
            return;
        }

        setIsSaving(true);
        try {
            const clientData: any = { ...formData };
            if (sedeId) clientData.sedeId = sedeId;
            else delete clientData.sedeId;

            const payload = new FormData();
            payload.append("data", JSON.stringify(clientData));
            clientFiles.forEach(file => {
                payload.append("files", new File([file], `DocID_${file.name}`, { type: file.type }));
            });
            const res = await ClientService.createMultipart(payload);

            if (res && res.success !== false && res.data) {
                addToast("Cliente creato con successo", "success");
                onCreated(res.data);
            } else {
                addToast(res?.message || "Errore salvataggio", "error");
            }
        } catch (error) {
            addToast("Errore di connessione", "error");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const isAzienda = formData.tipo === 'Azienda';
    const diTerziChecked = isAzienda || formData.perContoDiTerzi;
    const showSubForm = isAzienda || formData.perContoDiTerzi;
    const inputCls = "w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center flex-shrink-0">
                    <div>
                        <h2 className="text-base font-bold text-gray-900">Nuovo Cliente</h2>
                        <p className="text-xs text-gray-500 mt-0.5">Compila i dati del cliente. Verrà aggiunto alla rubrica e selezionato automaticamente.</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" autoComplete="off">
                    <div className="p-6 space-y-6">

                        {/* Tipo cliente */}
                        <div className="bg-white p-4 rounded-xl border border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-3">Tipo Cliente</label>
                            <div className="flex gap-4">
                                <label className={`flex-1 p-4 border rounded-xl cursor-pointer transition-all flex items-center justify-center gap-2 ${formData.tipo === 'Privato' ? 'border-blue-500 bg-blue-50 text-blue-700 font-bold' : 'hover:bg-gray-50'}`}>
                                    <input type="radio" className="hidden" name="tipo" checked={formData.tipo === 'Privato'} onChange={() => handleTipoChange('Privato')} />
                                    <User size={18}/> Privato
                                </label>
                                <label className={`flex-1 p-4 border rounded-xl cursor-pointer transition-all flex items-center justify-center gap-2 ${formData.tipo === 'Azienda' ? 'border-purple-500 bg-purple-50 text-purple-700 font-bold' : 'hover:bg-gray-50'}`}>
                                    <input type="radio" className="hidden" name="tipo" checked={formData.tipo === 'Azienda'} onChange={() => handleTipoChange('Azienda')} />
                                    <Building2 size={18}/> Azienda
                                </label>
                            </div>
                        </div>

                        {/* Dati anagrafici */}
                        <div className="bg-white p-4 rounded-xl border border-gray-200 space-y-4">
                            <h3 className="font-bold text-gray-900 border-b pb-2">
                                {isAzienda ? 'Dati Referente (Persona Fisica)' : 'Dati Anagrafici'}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                                    <input required type="text" autoComplete="new-password" className={inputCls} value={formData.nome} onChange={e => set('nome', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                                    <input required type="text" autoComplete="new-password" className={inputCls} value={formData.surname} onChange={e => set('surname', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Luogo di Nascita</label>
                                    <input type="text" autoComplete="new-password" className={inputCls} placeholder="Es. Roma" value={formData.luogoNascita} onChange={e => set('luogoNascita', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita</label>
                                    <input type="date" autoComplete="new-password" className={inputCls} value={formData.dataNascita} onChange={e => set('dataNascita', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                                    <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} value={formData.codiceFiscale} onChange={e => set('codiceFiscale', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                                    <input type="text" autoComplete="new-password" className={inputCls}
                                           value={isAzienda ? formData.telefonoReferente : formData.telefono}
                                           onChange={e => set(isAzienda ? 'telefonoReferente' : 'telefono', e.target.value)} />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input type="email" autoComplete="new-password" className={inputCls}
                                           value={isAzienda ? formData.emailReferente : formData.email}
                                           onChange={e => set(isAzienda ? 'emailReferente' : 'email', e.target.value)} />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo di Residenza</label>
                                    <div className="relative">
                                        <MapPin className="absolute left-2.5 top-2.5 text-gray-400" size={16}/>
                                        <input type="text" autoComplete="new-password" className={`${inputCls} pl-8`}
                                               value={isAzienda ? formData.indirizzoReferente : formData.indirizzo}
                                               onChange={e => set(isAzienda ? 'indirizzoReferente' : 'indirizzo', e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
                                    <input type="text" autoComplete="new-password" className={inputCls}
                                           value={isAzienda ? formData.cittaReferente : formData.citta}
                                           onChange={e => set(isAzienda ? 'cittaReferente' : 'citta', e.target.value)} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                                        <input type="text" autoComplete="new-password" className={inputCls}
                                               value={isAzienda ? formData.capReferente : formData.cap}
                                               onChange={e => set(isAzienda ? 'capReferente' : 'cap', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Prov.</label>
                                        <input type="text" autoComplete="new-password" maxLength={2} className={`${inputCls} uppercase`}
                                               value={isAzienda ? formData.provinciaReferente : formData.provincia}
                                               onChange={e => set(isAzienda ? 'provinciaReferente' : 'provincia', e.target.value)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Agisce per conto di */}
                        <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-100 space-y-4">
                            <h3 className="font-bold text-amber-800 border-b border-amber-200 pb-2 flex items-center gap-2">
                                <UserCheck size={18}/> Agisce per conto di {isAzienda && <span className="text-xs font-normal text-amber-600">(obbligatorio per Azienda)</span>}
                            </h3>

                            <div className="grid grid-cols-2 gap-3">
                                <label
                                    className={`flex items-center gap-2 p-3 border rounded-lg ${isAzienda ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed' : !formData.perContoDiTerzi ? 'border-blue-400 bg-blue-50 cursor-pointer' : 'border-gray-200 hover:bg-gray-50 cursor-pointer'}`}
                                    title={isAzienda ? "Per un cliente Azienda il referente agisce sempre per conto dell'azienda" : undefined}
                                >
                                    <input type="radio" className="text-blue-600" checked={!diTerziChecked} disabled={isAzienda} onChange={() => set('perContoDiTerzi', false)} />
                                    <span className="text-sm font-bold">Proprio</span>
                                </label>
                                <label className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer ${diTerziChecked ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                    <input type="radio" className="text-amber-600" checked={diTerziChecked} onChange={() => !isAzienda && set('perContoDiTerzi', true)} />
                                    <span className="text-sm font-bold">Di terzi</span>
                                </label>
                            </div>

                            {showSubForm && (
                                isAzienda ? (
                                    <div className="space-y-3 animate-in fade-in">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Denominazione (Ragione Sociale) *</label>
                                            <input required type="text" autoComplete="new-password" className={inputCls} value={formData.ragioneSociale} onChange={e => set('ragioneSociale', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Sede Legale</label>
                                            <div className="relative">
                                                <MapPin className="absolute left-2.5 top-2.5 text-gray-400" size={16}/>
                                                <input type="text" autoComplete="new-password" className={`${inputCls} pl-8`} value={formData.indirizzo} onChange={e => set('indirizzo', e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.cap} onChange={e => set('cap', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Comune</label>
                                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.citta} onChange={e => set('citta', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Prov.</label>
                                                <input type="text" autoComplete="new-password" maxLength={2} className={`${inputCls} uppercase`} value={formData.provincia} onChange={e => set('provincia', e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA</label>
                                                <input type="text" autoComplete="new-password" className={`${inputCls} font-mono uppercase`} value={formData.partitaIva} onChange={e => set('partitaIva', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Codice SDI</label>
                                                <input type="text" autoComplete="new-password" maxLength={7} className={`${inputCls} uppercase font-mono`} value={formData.codiceSdi} onChange={e => set('codiceSdi', e.target.value)} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">PEC</label>
                                            <input type="email" autoComplete="new-password" className={inputCls} value={formData.pec} onChange={e => set('pec', e.target.value)} />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3 animate-in fade-in">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Denominazione</label>
                                            <input type="text" autoComplete="new-password" className={inputCls} value={formData.denominazioneTerzi} onChange={e => set('denominazioneTerzi', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Sede / Residenza</label>
                                            <input type="text" autoComplete="new-password" className={inputCls} placeholder="Via, Città..." value={formData.sedeTerzi} onChange={e => set('sedeTerzi', e.target.value)} />
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.capTerzi} onChange={e => set('capTerzi', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Comune</label>
                                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.comuneTerzi} onChange={e => set('comuneTerzi', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Prov.</label>
                                                <input type="text" autoComplete="new-password" maxLength={2} className={`${inputCls} uppercase`} value={formData.provinciaTerzi} onChange={e => set('provinciaTerzi', e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">P.IVA / C.F.</label>
                                                <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} value={formData.pivaOrCfTerzi} onChange={e => set('pivaOrCfTerzi', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Codice SDI</label>
                                                <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} value={formData.codiceSdiTerzi} onChange={e => set('codiceSdiTerzi', e.target.value)} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Mail / PEC Soggetto Terzo</label>
                                            <input type="email" autoComplete="new-password" className={inputCls} value={formData.emailTerzi} onChange={e => set('emailTerzi', e.target.value)} />
                                        </div>
                                    </div>
                                )
                            )}
                        </div>

                        {/* Documento d'identità */}
                        <div className="bg-white p-4 rounded-xl border border-gray-200 space-y-4">
                            <h3 className="font-bold text-gray-900 border-b pb-2">Documento d'Identità</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Documento</label>
                                    <select className={`${inputCls} bg-white`} value={formData.docTipo} onChange={e => set('docTipo', e.target.value)}>
                                        <option value="Carta Identità">Carta d'Identità</option>
                                        <option value="Patente">Patente di Guida</option>
                                        <option value="Passaporto">Passaporto</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Numero Documento</label>
                                    <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} value={formData.docNumero} onChange={e => set('docNumero', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Rilasciato Da</label>
                                    <input type="text" autoComplete="new-password" className={inputCls} placeholder="Es. Comune di Roma" value={formData.docRilascioDa} onChange={e => set('docRilascioDa', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Rilascio</label>
                                    <input type="date" autoComplete="new-password" className={inputCls} value={formData.docDataRilascio} onChange={e => set('docDataRilascio', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Scadenza (Opzionale)</label>
                                    <input type="date" autoComplete="new-password" className={inputCls} value={formData.docDataScadenza} onChange={e => set('docDataScadenza', e.target.value)} />
                                </div>
                            </div>

                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                <label className="block text-sm font-bold text-slate-800 mb-3">Acquisizione Immagini (Fronte/Retro e C.F.)</label>
                                <label className={`flex flex-col items-center justify-center gap-2 bg-white border-2 border-dashed ${bypassDoc ? 'border-gray-200 opacity-50 cursor-not-allowed' : 'border-blue-300 hover:bg-blue-50 hover:border-blue-500 cursor-pointer'} p-6 rounded-xl transition-all`}>
                                    <input type="file" className="hidden" accept="image/*,application/pdf" capture="environment" multiple disabled={bypassDoc}
                                           onChange={(e) => { if (e.target.files) setClientFiles(prev => [...prev, ...Array.from(e.target.files!)]); }} />
                                    <Camera size={28} className={bypassDoc ? "text-gray-300" : "text-blue-500"}/>
                                    <span className="text-sm font-bold text-gray-700 text-center">Scatta foto dal Tablet o Carica file</span>
                                    <span className="text-xs text-gray-400 text-center">(Tocca qui per aprire la fotocamera)</span>
                                </label>
                                {clientFiles.length > 0 && !bypassDoc && (
                                    <div className="mt-4 space-y-2">
                                        {clientFiles.map((f, i) => (
                                            <FilePreviewItem key={i} file={f}
                                                onRemove={() => setClientFiles(prev => prev.filter((_, idx) => idx !== i))}
                                                onPreview={setPreviewFile}/>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-4 pt-4 border-t border-slate-200">
                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <input type="checkbox" className="mt-1 w-4 h-4 rounded text-amber-500 focus:ring-amber-500 border-gray-300 cursor-pointer"
                                               checked={bypassDoc} onChange={e => { setBypassDoc(e.target.checked); if (e.target.checked) setClientFiles([]); }} />
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-gray-800 group-hover:text-amber-600 transition-colors">Prosegui senza caricare i documenti d'identità</span>
                                            <span className="text-xs text-gray-500 mt-0.5">(Seleziona se hai già fatto le fotocopie in ufficio)</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-xl border border-gray-200">
                            <ColoreCalendarioField value={formData.coloreCalendario} onChange={v => set('coloreCalendario', v)} />
                        </div>
                    </div>

                    <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 justify-end flex-shrink-0 sticky bottom-0">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg font-bold hover:bg-gray-50">Annulla</button>
                        <button type="submit" disabled={isSaving} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2 disabled:opacity-60">
                            {isSaving ? <><Loader2 size={16} className="animate-spin"/> Salvataggio…</> : <><Save size={16}/> Crea e seleziona</>}
                        </button>
                    </div>
                </form>
            </div>

            {previewFile && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setPreviewFile(null)}>
                    <div className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center justify-center" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setPreviewFile(null)} className="absolute -top-12 right-0 text-white hover:text-gray-300 bg-black/50 p-2 rounded-full transition-colors">
                            <X size={24}/>
                        </button>
                        {previewFile.type.startsWith('image/') ? (
                            <img src={URL.createObjectURL(previewFile)} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" alt="Preview" />
                        ) : previewFile.type === 'application/pdf' ? (
                            <iframe src={URL.createObjectURL(previewFile)} className="w-full h-[85vh] rounded-lg shadow-2xl bg-white" title="PDF Preview" />
                        ) : (
                            <div className="bg-white p-8 rounded-xl flex flex-col items-center shadow-2xl">
                                <FileText size={64} className="text-gray-400 mb-4"/>
                                <p className="text-lg font-bold text-gray-900 text-center">{previewFile.name}</p>
                                <p className="text-sm text-gray-500 mt-2">Anteprima non disponibile per questo formato.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClienteCreateModal;
